// Due Date Notification Service
// Checks for upcoming due dates and shows notifications

import { PushNotificationService } from './pushNotifications';
import { loadNotebooks } from '@/lib/organizerRepository';

interface DueItem {
  id: string;
  title: string;
  dueDate: string;
  category: string;
  categoryLabel: string;
  classTitle: string;
  hoursUntilDue: number;
  isOverdue?: boolean;
  daysOverdue?: number;
}

export class DueDateNotificationService {
  private static lastCheckedItems: Set<string> = new Set();
  private static lastCheckedOverdueItems: Set<string> = new Set();
  private static checkInterval: NodeJS.Timeout | null = null;

  /**
   * Start checking for due dates (runs every hour)
   */
  static startDueDateChecker(userId: string): void {
    if (this.checkInterval) {
      console.log('[DueDateNotifications] Already running');
      return;
    }

    console.log('[DueDateNotifications] Starting due date checker');
    
    // Check immediately
    this.checkDueDates(userId);
    this.checkOverdueItems(userId);
    
    // Then check every hour
    this.checkInterval = setInterval(() => {
      this.checkDueDates(userId);
      this.checkOverdueItems(userId);
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * Stop checking for due dates
   */
  static stopDueDateChecker(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('[DueDateNotifications] Stopped due date checker');
    }
  }

  /**
   * Check for items due in next 24 hours
   */
  static async checkDueDates(userId: string): Promise<void> {
    try {
      console.log('[DueDateNotifications] Checking due dates for user:', userId);
      
      // Load user's notebooks
      const notebooks = await loadNotebooks(userId);
      
      if (!notebooks || notebooks.length === 0) {
        console.log('[DueDateNotifications] No notebooks found');
        return;
      }

      const now = new Date();
      const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const dueSoonItems: DueItem[] = [];

      // Check each notebook
      for (const notebook of notebooks) {
        const classTitle = notebook.title || notebook.name || 'Class';
        
        // Check each category
        for (const category of notebook.categories || []) {
          const categoryLabel = category.label || category.name || 'Item';
          
          // Check upcoming items
          const upcomingItems = category.buckets?.upcoming || [];
          
          for (const item of upcomingItems) {
            if (!item.dueDate) continue;
            
            const dueDate = new Date(item.dueDate);
            
            // Check if due within next 24 hours
            if (dueDate > now && dueDate <= twentyFourHoursFromNow) {
              const hoursUntilDue = Math.round((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60));
              
              dueSoonItems.push({
                id: item.id,
                title: item.title,
                dueDate: item.dueDate,
                category: category.id,
                categoryLabel,
                classTitle,
                hoursUntilDue,
              });
            }
          }
        }
      }

      console.log('[DueDateNotifications] Found', dueSoonItems.length, 'items due soon');

      // Show notifications for new due items
      for (const item of dueSoonItems) {
        const itemKey = `${item.id}-${item.dueDate}`;
        
        // Only notify if we haven't notified about this item before
        if (!this.lastCheckedItems.has(itemKey)) {
          this.showDueSoonNotification(item);
          this.lastCheckedItems.add(itemKey);
        }
      }

      // Clean up old checked items (keep last 100)
      if (this.lastCheckedItems.size > 100) {
        const itemsArray = Array.from(this.lastCheckedItems);
        this.lastCheckedItems = new Set(itemsArray.slice(-100));
      }

    } catch (error) {
      console.error('[DueDateNotifications] Error checking due dates:', error);
    }
  }

  /**
   * Show notification for item due soon
   */
  private static async showDueSoonNotification(item: DueItem): Promise<void> {
    const timeText = item.hoursUntilDue <= 1 
      ? 'in less than 1 hour' 
      : `in ${item.hoursUntilDue} hours`;
    
    const emoji = item.categoryLabel.toLowerCase().includes('assignment') ? '📝' :
                  item.categoryLabel.toLowerCase().includes('discussion') ? '💬' :
                  item.categoryLabel.toLowerCase().includes('experiential') ? '🧪' : '📚';
    
    // Use PERSISTENT notification for locked-screen support
    await PushNotificationService.showPersistentNotification(
      `${emoji} Due Soon: ${item.classTitle}`,
      {
        body: `${item.title} - Due ${timeText}`,
        tag: `due-soon-${item.id}`,
        requireInteraction: true,
        vibrate: [200, 100, 200],
        data: {
          type: 'due-date',
          itemId: item.id,
          url: '/organizer'
        },
        actions: [
          { action: 'open', title: 'Open', icon: '/icon-192.svg' },
          { action: 'dismiss', title: 'Dismiss', icon: '/icon-192.svg' }
        ]
      }
    );

    console.log('[DueDateNotifications] Showed notification for:', item.title);
  }

  /**
   * Check for overdue items
   */
  static async checkOverdueItems(userId: string): Promise<void> {
    try {
      console.log('[DueDateNotifications] Checking overdue items for user:', userId);
      
      // Load user's notebooks
      const notebooks = await loadNotebooks(userId);
      
      if (!notebooks || notebooks.length === 0) {
        console.log('[DueDateNotifications] No notebooks found for overdue check');
        return;
      }

      const now = new Date();
      const overdueItems: DueItem[] = [];

      // Check each notebook
      for (const notebook of notebooks) {
        const classTitle = notebook.title || notebook.name || 'Class';
        
        // Check each category
        for (const category of notebook.categories || []) {
          const categoryLabel = category.label || category.name || 'Item';
          
          // Check upcoming items (not completed yet)
          const upcomingItems = category.buckets?.upcoming || [];
          
          for (const item of upcomingItems) {
            if (!item.dueDate) continue;
            
            const dueDate = new Date(item.dueDate);
            
            // Check if overdue (past due date and not completed)
            if (dueDate < now) {
              const daysOverdue = Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
              
              overdueItems.push({
                id: item.id,
                title: item.title,
                dueDate: item.dueDate,
                category: category.id,
                categoryLabel,
                classTitle,
                hoursUntilDue: 0,
                isOverdue: true,
                daysOverdue,
              });
            }
          }
        }
      }

      console.log('[DueDateNotifications] Found', overdueItems.length, 'overdue items');

      // Show notifications for overdue items
      for (const item of overdueItems) {
        const itemKey = `overdue-${item.id}-${item.dueDate}`;
        
        // Only notify once per day for each overdue item
        if (!this.lastCheckedOverdueItems.has(itemKey)) {
          this.showOverdueNotification(item);
          this.lastCheckedOverdueItems.add(itemKey);
        }
      }

      // Clean up old checked items (keep last 100)
      if (this.lastCheckedOverdueItems.size > 100) {
        const itemsArray = Array.from(this.lastCheckedOverdueItems);
        this.lastCheckedOverdueItems = new Set(itemsArray.slice(-100));
      }

    } catch (error) {
      console.error('[DueDateNotifications] Error checking overdue items:', error);
    }
  }

  /**
   * Show notification for overdue item
   */
  private static async showOverdueNotification(item: DueItem): Promise<void> {
    const daysText = item.daysOverdue === 1 ? '1 day' : `${item.daysOverdue} days`;
    
    const emoji = item.categoryLabel.toLowerCase().includes('assignment') ? '📝' :
                  item.categoryLabel.toLowerCase().includes('discussion') ? '💬' :
                  item.categoryLabel.toLowerCase().includes('experiential') ? '🧪' : '📚';
    
    // Use PERSISTENT notification for locked-screen support
    await PushNotificationService.showPersistentNotification(
      `⚠️ OVERDUE: ${item.classTitle}`,
      {
        body: `${emoji} ${item.title} - Was due ${daysText} ago`,
        tag: `overdue-${item.id}`,
        requireInteraction: true,
        vibrate: [300, 100, 300, 100, 300], // More urgent vibration
        data: {
          type: 'overdue',
          itemId: item.id,
          url: '/organizer'
        },
        actions: [
          { action: 'open', title: 'Open', icon: '/icon-192.svg' },
          { action: 'dismiss', title: 'Dismiss', icon: '/icon-192.svg' }
        ]
      }
    );

    console.log('[DueDateNotifications] Showed overdue notification for:', item.title);
  }

  /**
   * Manually check for a specific user (useful for testing)
   */
  static async checkNow(userId: string): Promise<DueItem[]> {
    const notebooks = await loadNotebooks(userId);
    const now = new Date();
    const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const allItems: DueItem[] = [];

    for (const notebook of notebooks) {
      const classTitle = notebook.title || notebook.name || 'Class';
      
      for (const category of notebook.categories || []) {
        const categoryLabel = category.label || category.name || 'Item';
        const upcomingItems = category.buckets?.upcoming || [];
        
        for (const item of upcomingItems) {
          if (!item.dueDate) continue;
          
          const dueDate = new Date(item.dueDate);
          
          // Check if overdue
          if (dueDate < now) {
            const daysOverdue = Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
            
            allItems.push({
              id: item.id,
              title: item.title,
              dueDate: item.dueDate,
              category: category.id,
              categoryLabel,
              classTitle,
              hoursUntilDue: 0,
              isOverdue: true,
              daysOverdue,
            });
          }
          // Check if due within 24 hours
          else if (dueDate > now && dueDate <= twentyFourHoursFromNow) {
            const hoursUntilDue = Math.round((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60));
            
            allItems.push({
              id: item.id,
              title: item.title,
              dueDate: item.dueDate,
              category: category.id,
              categoryLabel,
              classTitle,
              hoursUntilDue,
              isOverdue: false,
            });
          }
        }
      }
    }

    // Sort: overdue first, then by time
    allItems.sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      if (a.isOverdue && b.isOverdue) return (b.daysOverdue || 0) - (a.daysOverdue || 0);
      return a.hoursUntilDue - b.hoursUntilDue;
    });

    return allItems;
  }
}
